import { useState } from 'react';
import { MdCreateNewFolder } from 'react-icons/md';
import {
  Button,
  Folder as MoveFolderModel,
  FolderSelect,
  Modal,
  ModalFooter,
  useToastNotifications,
} from '@nbirl/nbi-common-ui-libs';

import { useCurrentUser } from '../../../../../shared/context';
import { Permission } from '../../../../../shared/enum';
import { useErrorToast } from '../../../../../shared/hooks';
import { useFolderTree } from '../../context';
import { Folder } from '../../models';
import { useMoveFolderMutation } from '../../queries';
import { NewFolder } from '../new-folder.component';

type MoveFolderProps = {
  selectedFolder: Folder;
  isOpen?: boolean;
  onSuccess: () => void;
  onCloseModal: () => void;
};

export const MoveFolderModal: React.FC<MoveFolderProps> = ({
  selectedFolder,
  isOpen,
  onSuccess,
  onCloseModal,
}) => {
  const [folderDestination, setFolderDestination] =
    useState<MoveFolderModel | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const { data } = useFolderTree();
  const foldersTree = data ?? [];
  const moveFolderMutation = useMoveFolderMutation();
  const { addToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const { permissions } = useCurrentUser();

  const canCreateMainFolder = permissions.includes(
    Permission.ManageMainFolders
  );

  const onClose = () => {
    setFolderDestination(null);
    onCloseModal();
  };

  const handleMoveFolder = () => {
    if (!folderDestination) return;
    onClose();
    moveFolderMutation.mutate(
      {
        sourceFolderId: selectedFolder.id,
        destinationFolderId: folderDestination?.id,
      },
      {
        onSuccess: () => {
          addToast({
            variant: 'success',
            text: `Folder ${selectedFolder.name} successfully moved.`,
          });
          onSuccess();
        },
        onError: () => {
          showErrorToast(
            'Something went wrong. The folder could not be moved.'
          );
        },
      }
    );
  };

  return (
    <Modal
      heading={`Move "${selectedFolder.name}"`}
      isOpen={isOpen}
      onDismiss={onClose}
    >
      {showCreateFolder ? (
        <NewFolder
          parentId={folderDestination?.id}
          onSuccess={onSuccess}
          onClose={() => setShowCreateFolder(false)}
        />
      ) : (
        <FolderSelect
          folders={foldersTree}
          value={folderDestination}
          onChange={setFolderDestination}
        />
      )}
      <ModalFooter>
        {(folderDestination || canCreateMainFolder) && (
          <div className="flex-1">
            <Button
              variant="ghost"
              leftIcon={<MdCreateNewFolder />}
              onClick={() => setShowCreateFolder(true)}
            >
              Create folder
            </Button>
          </div>
        )}
        <Button disabled={!folderDestination} onClick={handleMoveFolder}>
          Move
        </Button>
      </ModalFooter>
    </Modal>
  );
};
